import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Banner,
  Button,
  ckConfirm,
  EmptyState,
  MenuButton,
  Panel,
  PanelGrid,
  ProgressBar,
  SearchInput,
  Segmented,
  SimpleTable,
  Spinner,
  StatGrid,
  StatTile,
  Tag,
  useCkToast,
} from "@NP-Webber/crm-kit/dist/next/index.js";
import { mailingApi, type BlastDto, type MailingOverviewDto } from "../../api/mailingApi";
import { ScreenHeader } from "../page/ScreenHeader";
import { useQuickCreate } from "../quickCreate";
import { useShell } from "../shell/ShellContext";
import { BlastComposer } from "./BlastComposer";
import { BlastReport } from "./BlastReport";
import { MailingForms } from "./MailingForms";
import { MailingLists } from "./MailingLists";
import { MailingSettingsPanel } from "./MailingSettingsPanel";
import { MailingTemplates } from "./MailingTemplates";
import { BLAST_STATUS, blastWhen, emptyDesign, errorMessage, fmtNum, pct } from "./mailingUi";
import "./mailing.css";

type Tab = "overview" | "blasts" | "templates" | "lists" | "forms" | "settings";
const TABS: Array<{ value: Tab; label: string }> = [
  { value: "overview", label: "סקירה" },
  { value: "blasts", label: "דיוורים" },
  { value: "templates", label: "תבניות" },
  { value: "lists", label: "נמענים" },
  { value: "forms", label: "טפסי הרשמה" },
  { value: "settings", label: "הגדרות" },
];

/** "new mailing" — optionally to customers chosen on the customers list (?customers=id,id / ?status=active) */
export function useNewBlast() {
  const navigate = useNavigate();
  const toast = useCkToast();
  return useCallback(
    async (opts: { templateId?: string; customerIds?: string[]; status?: string } = {}) => {
      try {
        const filters = opts.status ? { status: opts.status.split(",") } : undefined;
        const b = await mailingApi.blasts.create({
          name: "דיוור חדש",
          ...(opts.templateId ? { templateId: opts.templateId } : { design: emptyDesign() }),
          audience: { source: "customers", customerIds: opts.customerIds, filters },
        });
        navigate(`/mailing/${b.id}`);
      } catch (e) {
        toast.error(errorMessage(e, "לא הצלחנו ליצור דיוור"));
      }
    },
    [navigate, toast],
  );
}

function BlastRowStatus({ b }: { b: BlastDto }) {
  const st = BLAST_STATUS[b.status] ?? BLAST_STATUS.draft;
  return (
    <Tag tone={st.tone} icon={st.icon}>
      {st.label}
    </Tag>
  );
}

function Overview({ onTab }: { onTab: (t: Tab) => void }) {
  const navigate = useNavigate();
  const [data, setData] = useState<MailingOverviewDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    mailingApi
      .overview()
      .then(setData)
      .catch((e) => setError(errorMessage(e, "לא הצלחנו לטעון את סקירת הדיוור")));
  }, []);
  if (error) return <Banner tone="danger">{error}</Banner>;
  const q = data?.quota;
  const used = q ? Math.min(100, Math.round(((q.used + q.pending) / Math.max(1, q.quota)) * 100)) : 0;
  const l = data?.last30;
  return (
    <div className="ml-stack">
      {q?.suspended ? (
        <Banner tone="danger">השליחה בארגון מושעית{q.suspendedReason ? `: ${q.suspendedReason}` : ""}. דיוורים ממתינים לא יישלחו עד שההשעיה תוסר.</Banner>
      ) : null}
      <StatGrid min={160}>
        <StatTile label="נשלחו ב־30 יום" icon="mail" tone="primary" value={l ? fmtNum(l.sent) : undefined} loading={!data} />
        <StatTile label="שיעור פתיחה" icon="eye" tone="success" value={l ? pct(l.opened, l.sent) : undefined} loading={!data} />
        <StatTile label="שיעור הקלקה" icon="link" tone="violet" value={l ? pct(l.clicked, l.sent) : undefined} loading={!data} />
        <StatTile label="מנויים" icon="users" tone="neutral" value={data ? fmtNum(data.subscribers) : undefined} loading={!data} onClick={() => onTab("lists")} />
        <StatTile label="חסומים" icon="x" tone="neutral" value={data ? fmtNum(data.suppressions) : undefined} loading={!data} onClick={() => onTab("lists")} />
      </StatGrid>
      <PanelGrid cols={2}>
        <Panel title="מכסת החודש" icon="chart" sub={q ? `${fmtNum(q.used)} נשלחו · ${fmtNum(q.pending)} בתור · ${fmtNum(q.remaining)} נותרו` : undefined}>
          {q ? (
            <div className="ml-stack">
              <ProgressBar value={used} tone={used >= 90 ? "danger" : used >= 70 ? "warning" : "primary"} label={`${used}% מתוך ${fmtNum(q.quota)}`} />
              <small className="ml-muted">
                {q.allowOverage ? "מותרת חריגה מהמכסה." : "כשהמכסה נגמרת השליחה נעצרת עד החודש הבא. להגדלה — פנו לתמיכה."}
              </small>
            </div>
          ) : (
            <Spinner size={20} />
          )}
        </Panel>
        <Panel title="דיוורים אחרונים" icon="mail" flush actions={<Button size="sm" variant="ghost" onClick={() => onTab("blasts")}>כל הדיוורים</Button>}>
          <SimpleTable
            ariaLabel="דיוורים אחרונים"
            loading={!data}
            rows={data?.recent ?? []}
            rowKey="id"
            onRowClick={(r: BlastDto) => navigate(`/mailing/${r.id}`)}
            empty="עוד לא נשלחו דיוורים"
            columns={[
              { key: "name", label: "שם" },
              { key: "status", label: "מצב", render: (r: BlastDto) => <BlastRowStatus b={r} /> },
              { key: "opened", label: "פתיחות", align: "end", render: (r: BlastDto) => (r.counts.sent ? pct(r.counts.opened, r.counts.sent) : "") },
            ]}
          />
        </Panel>
      </PanelGrid>
    </div>
  );
}

function BlastList() {
  const navigate = useNavigate();
  const toast = useCkToast();
  const { allowed } = useShell();
  const canManage = allowed("mailing.manage");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [data, setData] = useState<{ items: BlastDto[]; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    mailingApi.blasts
      .list({ status, q, pageSize: 100 })
      .then((r) => {
        setData(r);
        setError(null);
      })
      .catch((e) => setError(errorMessage(e, "לא הצלחנו לטעון את הדיוורים")));
  }, [status, q]);
  useEffect(() => {
    const t = window.setTimeout(load, 200);
    return () => window.clearTimeout(t);
  }, [load]);

  const remove = async (b: BlastDto) => {
    if (!(await ckConfirm({ title: "למחוק את הדיוור?", message: `"${b.name}" יימחק${b.status === "sent" ? " יחד עם הדוח שלו" : ""}.`, confirmLabel: "מחיקה", danger: true }))) return;
    try {
      await mailingApi.blasts.remove(b.id);
      toast.success("נמחק");
      load();
    } catch (e) {
      toast.error(errorMessage(e, "המחיקה נכשלה"));
    }
  };

  return (
    <div className="ml-stack">
      <div className="ml-table-tools">
        <Segmented
          ariaLabel="מצב"
          value={status}
          onChange={setStatus}
          items={[
            { value: "", label: "הכול" },
            { value: "draft", label: "טיוטות" },
            { value: "scheduled,sending,paused", label: "פעילים" },
            { value: "sent", label: "נשלחו" },
          ]}
        />
        <SearchInput value={q} onChange={setQ} placeholder="חיפוש לפי שם או נושא…" />
      </div>
      {error ? <Banner tone="danger">{error}</Banner> : null}
      {data && !data.items.length && !status && !q ? (
        <EmptyState icon="mail" title="עדיין אין דיוורים">
          צרו דיוור ראשון — בחרו קהל מתוך הלקוחות או המנויים, עצבו את המייל ושלחו.
        </EmptyState>
      ) : (
        <Panel flush>
          <SimpleTable
            ariaLabel="דיוורים"
            loading={!data}
            rows={data?.items ?? []}
            rowKey="id"
            onRowClick={(r: BlastDto) => navigate(`/mailing/${r.id}`)}
            empty="אין דיוורים שמתאימים לסינון"
            columns={[
              { key: "name", label: "שם", render: (r: BlastDto) => <div className="ml-cell2"><b>{r.name}</b><small>{r.subject}</small></div> },
              { key: "status", label: "מצב", render: (r: BlastDto) => <BlastRowStatus b={r} /> },
              { key: "when", label: "מתי", render: (r: BlastDto) => <span className="ml-muted">{blastWhen(r)}</span> },
              { key: "recipients", label: "נמענים", align: "end", render: (r: BlastDto) => (r.counts.recipients ? fmtNum(r.counts.recipients) : "") },
              { key: "opened", label: "פתיחות", align: "end", render: (r: BlastDto) => (r.counts.sent ? pct(r.counts.opened, r.counts.sent) : "") },
              { key: "clicked", label: "הקלקות", align: "end", render: (r: BlastDto) => (r.counts.sent ? pct(r.counts.clicked, r.counts.sent) : "") },
              {
                key: "actions",
                label: "",
                width: 48,
                render: (r: BlastDto) =>
                  canManage ? (
                    <span onClick={(e) => e.stopPropagation()} role="presentation">
                      <MenuButton
                        align="end"
                        width={180}
                        items={[
                          {
                            key: "dup",
                            label: "שכפול",
                            icon: "copy",
                            onClick: () =>
                              void mailingApi.blasts
                                .duplicate(r.id)
                                .then((c) => navigate(`/mailing/${c.id}`))
                                .catch((e) => toast.error(errorMessage(e, "השכפול נכשל"))),
                          },
                          ...(["scheduled", "sending", "paused"].includes(r.status)
                            ? []
                            : [{ key: "del", label: "מחיקה", icon: "trash", danger: true, onClick: () => void remove(r) }]),
                        ]}
                        renderButton={(p) => <Button {...p} size="sm" variant="ghost" icon="more" iconOnly aria-label="פעולות" />}
                      />
                    </span>
                  ) : null,
              },
            ]}
          />
        </Panel>
      )}
    </div>
  );
}

/** /mailing/:id — the composer for a draft (or a form's welcome email), the report for everything else */
function BlastScreen({ id }: { id: string }) {
  const navigate = useNavigate();
  const [blast, setBlast] = useState<BlastDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setBlast(null);
    mailingApi.blasts
      .get(id)
      .then(setBlast)
      .catch((e) => setError(errorMessage(e, "הדיוור לא נמצא")));
  }, [id]);

  const editing = blast && (blast.status === "draft" || blast.kind === "welcome");
  return (
    <div className="ml-page">
      <ScreenHeader
        title={blast ? blast.name : "דיוור"}
        sub={blast ? (blast.kind === "welcome" ? "מייל ברוכים הבאים — נשלח אוטומטית למי שנרשם בטופס ואישר" : blast.subject || "ללא נושא") : undefined}
        more={[{ key: "back", label: "לכל הדיוורים", icon: "right", onClick: () => navigate("/mailing?tab=blasts") }]}
      />
      {error ? <Banner tone="danger">{error}</Banner> : null}
      {!blast && !error ? (
        <div className="ml-busy">
          <Spinner size={26} />
        </div>
      ) : null}
      {blast && editing ? (
        <>
          {blast.kind === "welcome" ? (
            <Panel flush>
              <div className="ml-welcome-stats">
                <span>
                  נשלח עד היום ל־<b>{fmtNum(blast.counts.sent)}</b> נרשמים
                </span>
                {blast.counts.sent ? <span>· נפתח {pct(blast.counts.opened, blast.counts.sent)}</span> : null}
              </div>
            </Panel>
          ) : null}
          <BlastComposer key={blast.id} blast={blast} onLaunched={setBlast} />
        </>
      ) : null}
      {blast && !editing ? <BlastReport key={blast.id} blast={blast} onChanged={setBlast} /> : null}
    </div>
  );
}

/** /mailing — "דיוור": mailings to customers / subscribers, templates, lists, signup forms, settings (ADR-0033). */
export function MailingPage() {
  const { id } = useParams<{ id?: string }>();
  const [params, setParams] = useSearchParams();
  const { allowed } = useShell();
  const canManage = allowed("mailing.manage");
  const newBlast = useNewBlast();
  const tab = (TABS.some((t) => t.value === params.get("tab")) ? params.get("tab") : "overview") as Tab;
  const setTab = (t: Tab) => setParams(t === "overview" ? {} : { tab: t }, { replace: true });

  // arriving from the customers list: /mailing/new?status=active or ?customers=id,id
  const navigate = useNavigate();
  const startedFromLink = useMemo(() => id === "new", [id]);
  useEffect(() => {
    if (!startedFromLink) return;
    if (!canManage) {
      navigate("/mailing", { replace: true });
      return;
    }
    const customers = params.get("customers");
    void newBlast({ customerIds: customers ? customers.split(",").filter(Boolean) : undefined, status: params.get("status") ?? undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startedFromLink]);

  useQuickCreate("mailing", canManage ? () => void newBlast() : null);

  if (startedFromLink) {
    return (
      <div className="ml-busy">
        <Spinner size={26} />
      </div>
    );
  }
  if (id) return <BlastScreen id={id} />;

  return (
    <div className="ml-page">
      <ScreenHeader
        sub="דיוורים ללקוחות ולמנויים — עם הסרה בלחיצה, מעקב פתיחות והקלקות, ותזמון"
        primary={canManage ? { label: "דיוור חדש", icon: "plus", onClick: () => void newBlast() } : undefined}
      />
      <div className="ml-tabs">
        <Segmented ariaLabel="לשוניות דיוור" value={tab} onChange={(v) => setTab(v as Tab)} items={TABS} />
      </div>
      {tab === "overview" ? <Overview onTab={setTab} /> : null}
      {tab === "blasts" ? <BlastList /> : null}
      {tab === "templates" ? <MailingTemplates onUse={(templateId) => void newBlast({ templateId })} /> : null}
      {tab === "lists" ? <MailingLists /> : null}
      {tab === "forms" ? <MailingForms /> : null}
      {tab === "settings" ? <MailingSettingsPanel /> : null}
    </div>
  );
}

export default MailingPage;
